import React, { useEffect, useState } from 'react'
import {
  FormControl,
  Paper,
  Select,
  MenuItem,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core'
import ActionBar from 'components/ActionBar'

import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  CalendarTodayOutlined,
  ForumOutlined
} from '@material-ui/icons'
import eventSlice, {
  fetchEvents,
  deleteEventById,
  ALL_DATES_FILTER,
  fetchEventTypes
} from 'store/event'
import venueSlice, { fetchVenues } from 'store/venue'
import { renderCol } from 'utils/renderColumns'

import ViewHeader from 'components/ViewHeader'
import ResourceNotFound from 'components/ResourceNotFound'
import getTableOptions from 'utils/getTableOptions'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import { convertTimestampToLocalString } from 'utils/times'
import Wrapper from 'components/Wrapper'
import moment from 'moment'
import { getContrastColor } from 'utils/colors'
import { toast } from 'react-toastify'
import ConfirmModal from 'components/ConfirmationModal'

const useStyles = makeStyles({
  dateFilterDropdown: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  dateFilterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '4px',
    fontWeight: 'bold'
  }
})

const EventListing = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const [loading, setLoading] = useState(false)
  const [deletedEventId, setDeletedEventId] = useState('')
  const [deletingEvent, setDeletingEvent] = useState(false)
  const [formattedEvents, setFormattedEvents] = useState([])
  const [cardColors, setCardColors] = useState([])
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])

  const { events, eventTypes, selectedDateFilter, currentPage, pageSize } = useSelector(
    (state) => state.event
  )
  const { user } = useSelector((state) => state.auth)
  const { venues } = useSelector((state) => state.venue)

  const [eventsWithType, setEventsWithType] = useState([])

  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    const unsubVenues = fetchVenues(user?.conferenceId, (venuesData) => {
      dispatch(venueSlice.actions.setVenues(venuesData))
    })
    dispatch(fetchEventTypes())

    return () => unsubVenues && unsubVenues()
  }, [dispatch, user?.conferenceId])

  useEffect(() => {
    const card_colors = events.reduce((prev, curr) => {
      const bgColor = eventTypes.find((color) => color.id === curr.cardStyle)
      prev[curr.id] = {
        backgroundColor: bgColor?.bgColor,
        color: getContrastColor(bgColor?.bgColor ? bgColor?.bgColor : '#FFFFFF')
      }
      return prev
    }, {})
    setCardColors(card_colors)
  }, [eventTypes, events])

  useEffect(() => {
    if (eventsWithType.length && conference) {
      const filters = []
      const data = eventsWithType.reduce((prev, cur) => {
        const date = moment(cur.startDate).tz(conference?.timezone).format('M/D/YYYY')
        if (!prev[date]) {
          filters.push(date)
          prev[date] = [cur]
        } else {
          prev[date].push(cur)
        }
        return prev
      }, {})
      data[ALL_DATES_FILTER] = eventsWithType

      if (!filters.includes(selectedDateFilter)) {
        dispatch(eventSlice.actions.setSelectedDateFilter(ALL_DATES_FILTER))
      }
      setDateFilters([ALL_DATES_FILTER, ...filters])
      setFormattedEvents(data)
    }
  }, [eventsWithType, conference])

  useEffect(() => {
    setEventsWithType(
      events.map((event) => ({
        ...event,
        eventVenue: venues.find((type) => type.id === event.eventVenue)
      }))
    )
  }, [events, venues])

  useEffect(() => {
    setLoading(true)
    fetchEvents(user?.conferenceId)
      .then((data) => {
        dispatch(eventSlice.actions.setEvents(data))
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
    return () => dispatch(eventSlice.actions.setEvents([]))
  }, [user?.conferenceId])

  const handleReloadEvents = async () => {
    const data = await fetchEvents(user?.conferenceId)
    dispatch(eventSlice.actions.setEvents(data))
  }

  const closeModal = () => {
    setDeletedEventId('')
  }

  const handleDeleteEvent = async () => {
    if (deletedEventId) {
      setDeletingEvent(true)
      await dispatch(deleteEventById(deletedEventId))
      await handleReloadEvents()
      toast.success('Event deleted successfully.')
    }
    setDeletingEvent(false)
  }

  const tableHead = [
    {
      name: '',
      options: {
        customBodyRender: (value) => value,
        display: false
      }
    },
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'Title',
    'Venue',
    'Type',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    { name: 'Polls', options: { display: conference?.havePolls } },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (events) =>
    events.map((event) => [
      event?.id,
      event?.srNo,
      event?.title,
      event?.eventVenue?.venueText,
      event?.eventType === 'haveTalks'
        ? 'Contains Talks'
        : event?.eventType === 'haveSessions'
        ? 'Contains Sessions'
        : 'Standalone',
      event.startDate,
      event.endDate,
      event.havePolls ? 'Yes' : 'No',
      <div style={{ width: 100 }}>
        {event?.eventType === 'haveSessions' ? (
          <Tooltip title="Sessions">
            <CalendarTodayOutlined
              color="inherit"
              style={{ marginRight: 10, cursor: 'pointer' }}
              onClick={() => history.push(`${location.pathname}/${event.id}/sessions`)}
            />
          </Tooltip>
        ) : event?.eventType === 'haveTalks' ? (
          <Tooltip title="Talks">
            <ForumOutlined
              color="inherit"
              style={{ marginRight: 10, cursor: 'pointer' }}
              onClick={() => history.push(`${location.pathname}/${event.id}/talks`)}
            />
          </Tooltip>
        ) : (
          <></>
        )}
        <Tooltip title="Edit">
          <EditIcon
            color="inherit"
            style={{ marginRight: 10, cursor: 'pointer' }}
            onClick={() => history.push(`${location.pathname}/edit/${event.id}`)}
          />
        </Tooltip>

        <Tooltip title="Delete">
          <DeleteIcon
            color="inherit"
            style={{ marginRight: 10, cursor: 'pointer' }}
            onClick={() => setDeletedEventId(event?.id)}></DeleteIcon>
        </Tooltip>
      </div>
    ])

  const options = getTableOptions()

  const updatedOptions = {
    ...options,
    setRowProps: (row) => {
      return {
        style: {
          backgroundColor: cardColors[row[0]]?.backgroundColor,
          color: cardColors[row[0]]?.color
        }
      }
    }
  }
  return (
    <>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={4} lg={3} className={classes.dateFilterDropdown}>
          <Typography className={classes.dateFilterLabel}>View events for: </Typography>
          <FormControl fullWidth variant="outlined">
            <Select
              value={selectedDateFilter}
              onChange={(event) => {
                const value = event.target.value
                dispatch(eventSlice.actions.setSelectedDateFilter(value))
              }}>
              {dateFilters.map((filter) => (
                <MenuItem value={filter} key={filter}>
                  {filter}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <ViewHeader title="Events">
        <ActionBar
          actionText="New Event"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Events"
        />
      </ViewHeader>
      <Wrapper inProgress={loading}>
        <Paper variant="outlined" style={{ marginTop: '20px' }}>
          {eventsWithType && eventsWithType.length ? (
            <BasicTable
              data={formatTableData(formattedEvents[selectedDateFilter] || [])}
              columns={tableHead}
              options={{
                ...updatedOptions,
                onChangePage: (page) => dispatch(eventSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(eventSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound message="No event available" />
          )}
        </Paper>
      </Wrapper>
      <ConfirmModal
        dialogTitle="Delete event"
        dialogText={`Are you sure you want to delete this event? \n This will delete all the sessions and talks contained in the event. \n This action can not be reverted.`}
        open={Boolean(deletedEventId)}
        closeModal={closeModal}
        handleConfirm={handleDeleteEvent}
        inProgress={deletingEvent}
      />
    </>
  )
}

export default EventListing
